<template>
  <div class="tab-rail-wrap" :class="{ 'tab-rail-wrap--has-actions': hasActions(), 'tab-rail-wrap--tight': tight }">
    <div class="tab-rail" ref="rail" role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :key="'TR' + index"
        type="button"
        role="tab"
        class="tab-rail__tab"
        :class="{ 'tab-rail__tab--active': index === value }"
        :aria-selected="index === value ? 'true' : 'false'"
        @click="onSelect(index)"
      >
        <span class="tab-rail__label">
          <span class="d-none d-md-inline">{{ tab.name }}</span>
          <span class="d-inline d-md-none">{{ tab.shortName || tab.name }}</span>
          <span v-if="tab.count != null" class="tab-rail__count">{{ tab.count }}</span>
        </span>
      </button>
    </div>
    <!-- Trailing actions (overflow menu, buttons): outside the scrolling rail
         so they stay put, with the hairline running underneath them -->
    <div v-if="hasActions()" class="tab-rail__actions">
      <slot name="actions"></slot>
    </div>
    <!-- Outside the scroll element so the edge fade mask cannot erase it -->
    <span v-if="canScrollRight" class="tab-rail-wrap__more" aria-hidden="true">
      <i class="tim-icons icon-minimal-right"></i>
    </span>
  </div>
</template>

<script>
/**
 * TabRail
 * Underline tab rail: a full-width hairline caps the row, the active tab
 * gets a thick primary underline, and hovering an inactive tab shows a
 * soft rounded pill behind its label.
 * Mobile (below md): equal-width columns with short labels; nothing
 * scrolls or clips by construction. Fits up to ~5 short labels; past
 * that a page needs a drill-in pattern, not a scrolling strip.
 *
 * @prop {Array}  tabs  [{ name, shortName?, count? }] count renders a small
 *                number chip after the label (e.g. how many rows the tab lists)
 * @prop {Number} value active tab index (v-model)
 * @prop {Boolean} tight with trailing actions, keep the rail only as tall as its
 *                tabs; the actions center between the card top and the hairline
 *                (for cards where the rail is the first row)
 * @slot actions  trailing controls on the rail's right (e.g. an overflow menu);
 *                the hairline runs the full width, underneath them
 * @event input(index)
 */
export default {
  name: 'TabRail',
  props: {
    tabs: { type: Array, required: true },
    value: { type: Number, default: 0 },
    tight: { type: Boolean, default: false },
  },
  data() {
    return {
      canScrollRight: false,
    };
  },
  mounted() {
    this.fitTabs();
    this.updateScrollHint();
    this.onScroll = () => this.updateScrollHint();
    this.$refs.rail.addEventListener('scroll', this.onScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      // Defer the fit to the next frame: mutating layout synchronously inside
      // the observer callback triggers the "loop completed with undelivered
      // notifications" error. The flag collapses bursts to one fit per frame.
      this.resizeObserver = new ResizeObserver(() => {
        if (this.fitScheduled) return;
        this.fitScheduled = true;
        requestAnimationFrame(() => {
          this.fitScheduled = false;
          this.fitTabs();
          this.updateScrollHint();
        });
      });
      this.resizeObserver.observe(this.$refs.rail);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onScroll && this.$refs.rail) this.$refs.rail.removeEventListener('scroll', this.onScroll);
  },
  watch: {
    tabs() {
      this.$nextTick(this.fitTabs);
    },
  },
  methods: {
    // Read in the template on every render, never cached: a parent that adds or removes
    // the slot later (a per-tab action) re-renders this component with new $scopedSlots,
    // while a computed over $slots or $scopedSlots is evaluated once and never updates.
    hasActions() {
      return !!this.$scopedSlots.actions;
    },
    onSelect(index) {
      if (index !== this.value) this.$emit('input', index);
      this.$nextTick(() => {
        const rail = this.$refs.rail;
        const active = rail && rail.querySelector('.tab-rail__tab--active');
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    },
    // Squeeze to fit: tighten the gap between tabs first (20px down to 8px),
    // then shrink the font (14px down to 10px). Starts from full size on every
    // pass so growing the window restores it; past both floors the rail scrolls.
    fitTabs() {
      const el = this.$refs.rail;
      if (!el || !el.isConnected) return;
      // Below the mobile breakpoint the rail scrolls instead of squeezing;
      // reset any desktop squeeze so the media-query sizing applies.
      if (window.innerWidth < 768) {
        el.style.fontSize = '';
        el.style.removeProperty('--tab-gap');
        return;
      }
      let size = 14;
      let gap = 20;
      const apply = () => {
        el.style.fontSize = size + 'px';
        el.style.setProperty('--tab-gap', gap + 'px');
      };
      const overflows = () => {
        if (el.scrollWidth > el.clientWidth + 1) return true;
        const labels = el.querySelectorAll('.tab-rail__label');
        for (const label of labels) {
          if (label.scrollWidth > label.clientWidth + 1) return true;
        }
        return false;
      };
      apply();
      while ((gap > 8 || size > 10) && overflows()) {
        if (gap > 8) {
          gap -= 2;
        } else {
          size -= 1;
        }
        apply();
      }
    },
    // Scrollability affordance: fade the rail's content at the edge that has
    // more tabs (mask-based, so it works over any card background and theme).
    updateScrollHint() {
      const el = this.$refs.rail;
      if (!el || !el.isConnected) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 1) {
        el.style.webkitMaskImage = '';
        el.style.maskImage = '';
        this.canScrollRight = false;
        return;
      }
      const fadedLeft = el.scrollLeft > 1;
      const fadedRight = el.scrollLeft < maxScroll - 1;
      this.canScrollRight = fadedRight;
      const start = fadedLeft ? 'transparent 0, black 28px' : 'black 0';
      const end = fadedRight ? 'black calc(100% - 28px), transparent 100%' : 'black 100%';
      const mask = `linear-gradient(to right, ${start}, ${end})`;
      el.style.webkitMaskImage = mask;
      el.style.maskImage = mask;
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-rail {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);

  // Labels are never clipped. Desktop squeezes gap and font first (fitTabs);
  // past those floors, and always on mobile, the rail scrolls horizontally
  // with a hidden scrollbar and the edge fade from updateScrollHint.
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &__tab {
    appearance: none;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: inherit;
    color: rgba(255, 255, 255, 0.75);
    padding: 0 0 8px;
    margin-right: var(--tab-gap, 20px);
    position: relative;
    margin-bottom: -1px;
    transition: color 0.15s ease;
    // Tabs keep their full label; the rail scrolls when they do not fit.
    flex: 0 0 auto;

    &:focus {
      outline: none;
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 3px;
      border-radius: 2px;
      background: transparent;
      transition: background 0.15s ease;
    }

    &:hover {
      color: #ffffff;
    }

    // Stripe-style hover pill around the label
    &:hover:not(.tab-rail__tab--active) .tab-rail__label {
      background: rgba(125, 125, 125, 0.15);
    }

    @media (max-width: 767px) {
      // Fill the row when there is room, never shrink below the label.
      flex: 1 0 auto;
      margin-right: 0;
      font-size: 0.7rem;
    }
  }

  &__label {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 8px;
    transition: background 0.15s ease;
    white-space: nowrap;

    @media (max-width: 767px) {
      display: block;
      padding: 6px 6px;
      text-align: center;
    }
  }

  &__tab--active {
    color: #ffffff;
    font-weight: 500;

    &::after {
      background: #e14eca;
    }

    .tab-rail__count {
      background: rgba(225, 78, 202, 0.2);
      color: #e14eca;
    }
  }

  // Number chip after a label: theme-neutral gray, primary tint on the active tab.
  &__count {
    display: inline-block;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: 6px;
    border-radius: 10px;
    background: rgba(125, 125, 125, 0.25);
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 20px;
    text-align: center;
    vertical-align: middle;
    transition: background 0.15s ease, color 0.15s ease;

    @media (max-width: 767px) {
      margin-left: 4px;
    }
  }

}

.tab-rail-wrap {
  position: relative;
  // Flex-item consumers (flex-grow-1 rows) need this to allow shrinking.
  min-width: 0;

  // With trailing actions the hairline moves up to the wrap so it runs the
  // full width, under the actions too, instead of stopping at the last tab.
  &--has-actions {
    display: flex;
    align-items: flex-end;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    .tab-rail {
      border-bottom: none;
      flex: 1 1 auto;
      min-width: 0;
    }

    .tab-rail__actions {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      // Trailing controls sit centered between the card top (15px card-body
      // padding plus the 4px .btn margin) and the hairline: 15px here plus
      // the same 4px .btn margin below. The tabs stay anchored on the line.
      padding: 0 0 15px 12px;
    }

    // Opt-in: the actions no longer push the tabs down from the card top.
    // They reach 8px up into the card's 15px top padding and keep 8px under
    // them, which centers them between the card top and the hairline while
    // the rail stays only as tall as its tabs.
    &.tab-rail-wrap--tight .tab-rail__actions {
      margin-top: -8px;
      padding-bottom: 8px;
    }
  }

  // "More tabs" chevron: overlaid at the right edge, above the edge fade.
  &__more {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 9px;
    display: flex;
    align-items: center;
    color: #e14eca;
    font-size: 11px;
    pointer-events: none;
  }
}
</style>

<style>
.white-content .tab-rail {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}
.white-content .tab-rail-wrap--has-actions {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}
.white-content .tab-rail__tab {
  color: rgba(0, 0, 0, 0.75);
}
.white-content .tab-rail__tab:hover {
  color: rgba(0, 0, 0, 0.9);
}
.white-content .tab-rail__tab--active {
  color: rgba(0, 0, 0, 0.9);
}
</style>
